<template>
  <v-dialog
    width="600"
    v-model="opened"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    @keydown.esc="$emit('update:opened', false)"
    persistent
  >
    <!-- Meta: Uploader -->
    <input
      type="file"
      style="display: none"
      multiple
      :accept="customAcceptFileTypes"
      ref="selectedDocuments"
      @change="onDocumentsPicked"
    />
    <v-card flat>
      <v-card-title class="title pb-0 text-xs-left">
        Upload File
      </v-card-title>
      <v-card-text>
        <v-layout class="mb-4 text-xs-left" column>
          <v-flex>
            <span>File Type</span>
            <v-select
              v-model="selectedFileType"
              placeholder="Select File Type"
              class="default-input"
              :items="filteredSupportedFileTypes"
              item-text="name"
              item-value="value"
              solo
              hide-details
            />
          </v-flex>
          <v-flex
            v-if="selectedFileType === 'zipped_rollover_items'"
            class="mt-4"
          >
            <span>Tax Software</span>
            <v-select
              v-model="selectedImportMethod"
              placeholder="Select Tax Software"
              class="default-input"
              :items="supportedTaxSoftwaresForPriorYearImport"
              solo
              hide-details
            />
          </v-flex>
          <v-flex v-if="selectedFileType === 'other_document'" class="mt-4">
            <span>File Description</span>
            <v-text-field
              placeholder="Please provide a description of the file."
              type="text"
              v-model="otherFileName"
              solo
              hide-details
              class="default-input"
            />
          </v-flex>

          <v-flex v-if="selectedFileType === 'client_list'" class="mt-4">
            <span>Assign to</span>
            <v-select
              v-model="selectedAssignedFirmUserId"
              placeholder="Select Firm User to assign to"
              class="default-input"
              :items="firmUsersWithVarious"
              item-text="name"
              item-value="id"
              solo
              hide-details
            />
          </v-flex>
          <v-flex xs6 v-if="selectedFileType === 'client_list'" class="mt-4">
            <span>Client List Type</span>
            <v-select
              v-model="selectedClientListType"
              placeholder="Select Client List Type"
              class="default-input"
              :items="clientListTypes"
              item-text="name"
              item-value="value"
              solo
              hide-details
            />
          </v-flex>
        </v-layout>
        <v-layout
          v-if="isSorabanAdmin && selectedFileType === 'rollover_items_import'"
          class="mb-4"
        >
          <v-flex xs6>
            <v-select
              v-model="selectedOrganizerYear"
              placeholder="Select Tax Year to create Prior Years for"
              class="default-input"
              :items="yearOptions"
              solo
              hide-details
            />
          </v-flex>
          <v-flex xs6>
            <v-select
              v-model="selectedImportMethod"
              placeholder="Select Import Method"
              class="default-input"
              :items="supportedImportMethods"
              item-text="name"
              item-value="value"
              solo
              hide-details
            />
          </v-flex>
        </v-layout>
        <v-flex class="text-xs-left">
          <v-alert
            :value="true"
            type="info"
            outline
            v-if="
              selectedFileType === 'client_list' &&
                shouldUseNewSchemaValidations === true
            "
          >
            Please
            <v-tooltip top>
              <span
                slot="activator"
                @click="downloadIndividualImportTemplate()"
                style="cursor: pointer; text-decoration: underline"
                >download the template for Individuals</span
              >
              <span>Click to download</span>
            </v-tooltip>
            or
            <v-tooltip top>
              <span
                slot="activator"
                @click="downloadBusinessImportTemplate()"
                style="cursor: pointer; text-decoration: underline"
                >download the template for Businesses</span
              >
              <span>Click to download</span>
            </v-tooltip>
            (depending which list type you are uploading). Fill in the
            information of clients you want to import and upload it here or
            upload client list generated by your tax software. All files
            uploaded are encrypted and secure. Client List takes about 2
            business days to process.

            <br />
            <br />
            Requirements:
            <br />

            <ul>
              <li>It needs to be a .xlsx file</li>
              <li>
                We will need you to provide <strong>two</strong> sheets in the
                file with these exact names: "Entities" and "Individuals". Even
                if you are only importing one type (just individuals or
                entities), we will need you to provide both sheets. That way if
                one is empty, we'll know you're not importing that type.
              </li>
              <li>
                The "Individual" sheet should have each column found in the
                "Individual" sheet from the template
              </li>
              <li>
                The "Entity" sheet should have each column found in the "Entity"
                sheet from the template
              </li>
            </ul>
          </v-alert>

          <v-alert
            :value="true"
            type="info"
            outline
            v-if="
              selectedFileType === 'client_list' &&
                shouldUseNewSchemaValidations !== true
            "
          >
            Please
            <v-tooltip top>
              <span
                slot="activator"
                @click="downloadIndividualImportTemplate()"
                style="cursor: pointer; text-decoration: underline"
                >download the template for Individuals</span
              >
              <span>Click to download</span>
            </v-tooltip>
            or
            <v-tooltip top>
              <span
                slot="activator"
                @click="downloadBusinessImportTemplate()"
                style="cursor: pointer; text-decoration: underline"
                >download the template for Businesses</span
              >
              <span>Click to download</span>
            </v-tooltip>
            (depending which list type you are uploading). Fill in the
            information of clients you want to import and upload it here or
            upload client list generated by your tax software. All files
            uploaded are encrypted and secure. Client List takes about 2
            business days to process.
          </v-alert>

          <v-alert
            :value="true"
            type="info"
            outline
            v-if="selectedFileType === 'zipped_rollover_items'"
          >
            <span class="font-weight-bold error--text"
              >Organizers and backup files MUST be uploaded in a compressed zip
              folder.</span
            >
            It takes about 2 business days to process the rollover items, but
            may take longer depending on the number of clients.
          </v-alert>
        </v-flex>
        <v-layout align-center class="text-xs-left">
          <v-flex>
            <v-layout
              align-center
              :key="i"
              v-for="(doc, i) in documentsToImport"
            >
              <v-icon color="primary"> insert_drive_file </v-icon>
              <span class="ml-1 my-1">{{ doc.name }}</span>
              <v-spacer />
              <v-icon
                @click="documentsToImport.splice(i, 1)"
                style="cursor: pointer"
                color="error"
                >cancel</v-icon
              >
            </v-layout>
          </v-flex>
        </v-layout>
        <v-layout align-center v-if="selectedFileType">
          <v-flex @drop.prevent="addFile" @dragover.prevent>
            <v-layout class="drag-and-drop mt-2 mb-3" style="min-height: 200px">
              <v-layout column align-center justify-center>
                <div>
                  <v-icon class="primary--text">cloud_upload</v-icon>
                </div>
                <div>
                  <span @click="pickDocuments"
                    >Drag and drop files here or
                    <span
                      class="clickable primary--text"
                      style="cursor: pointer"
                      >browse</span
                    ></span
                  >
                </div>
              </v-layout>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          class="text-capitalize primary"
          @click="uploadUserImport()"
          :loading="documentsImporting"
          :disabled="!documentsUploadable"
        >
          Upload
        </v-btn>
        <v-btn
          class="text-capitalize error--text"
          outline
          @click="$emit('update:opened', false)"
        >
          {{ $t('cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  priorYearItemsImportMethods,
  yearOptions,
} from '@/constants/variables';
import isEmpty from 'lodash/isEmpty';
import UtilsMixins from '@/mixins/UtilsMixins';
import AdminFunctionsMixin from '@/mixins/AdminFunctionsMixin';
import { mapGetters } from 'vuex';
import API from '@/api';
import * as XLSX from 'xlsx';

export default {
  props: {
    opened: {
      type: Boolean,
    },
    firmUsers: {
      type: Array,
    },
  },
  mixins: [UtilsMixins, AdminFunctionsMixin],

  data() {
    return {
      isEmpty,
      yearOptions,
      supportedFileTypes: [
        {
          name: 'Client List',
          value: 'client_list',
        },
        {
          name: 'Staff List',
          value: 'staff_list',
        },
        {
          name: 'Zipped Organizer/Backup Files for Rollover Items',
          value: 'zipped_rollover_items',
        },
        {
          name: 'Other Document',
          value: 'other_document',
        },
        {
          name: 'Rollover Items Import',
          value: 'rollover_items_import',
        },
      ],
      clientListTypes: [
        {
          name: 'Individuals (1040) List',
          value: 'individuals',
        },
        {
          name: 'Entities (1120, 1065, 1041, etc) List',
          value: 'entities',
        },
      ],
      selectedClientListType: null,
      priorYearItemsImportMethods,
      selectedOrganizerYear: 2024,
      selectedFileType: null,
      selectedImportMethod: null,
      documentsToImport: [],
      documentsImporting: false,
      otherFileName: '',
      selectedAssignedFirmUserId: null,
      // NOTE to devs: you can change this to false to go back to the "old" way of allowing imports
      shouldUseNewSchemaValidations: false,
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentFirm: 'currentFirm',
    }),
    firmUsersWithVarious() {
      return [
        {
          id: 0,
          name: 'Various (add firm user names as a column in client list)',
        },
      ].concat(this.firmUsers);
    },
    filteredSupportedFileTypes() {
      if (this.isSorabanAdmin) {
        return this.supportedFileTypes;
      } else {
        return this.supportedFileTypes.filter(
          type => type.value !== 'rollover_items_import',
        );
      }
    },
    customAcceptFileTypes() {
      if (this.selectedFileType === 'zipped_rollover_items') {
        return '.zip';
      } else if (this.selectedFileType === 'rollover_items_import') {
        if (this.selectedImportMethod === 'taxprep_return') {
          return '.csv';
        } else {
          return 'application/pdf, image/*';
        }
      } else {
        return '*';
      }
    },
    documentsUploadable() {
      if (this.documentsToImport.length === 0) return false;

      if (this.selectedFileType === 'other_document') {
        return !isEmpty(this.otherFileName);
      } else if (
        this.selectedFileType === 'zipped_rollover_items' ||
        this.selectedFileType === 'rollover_items_import'
      ) {
        return this.selectedImportMethod;
      } else {
        return true;
      }
    },
    supportedImportMethods() {
      return this.priorYearItemsImportMethods
        .slice(1)
        .filter(a => a.bulk_supported);
    },
    supportedTaxSoftwaresForPriorYearImport() {
      return this.priorYearItemsImportMethods
        .filter(method => method.bulk_supported)
        .map(method => method.name);
    },
  },

  methods: {
    async downloadIndividualImportTemplate() {
      try {
        const response = await API.advisor.downloadIndividualImportTemplate();
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          window.open(response.data.document.data.link, '_blank');
        }
      } catch (e) {
        console.log(e);
      }
    },
    async downloadBusinessImportTemplate() {
      try {
        const response = await API.advisor.downloadBusinessImportTemplate();
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          window.open(response.data.document.data.link, '_blank');
        }
      } catch (e) {
        console.log(e);
      }
    },
    async addFile(e) {
      this.documentsImporting = true;
      let droppedFiles = e.dataTransfer.files;
      if (!droppedFiles) return;

      this.documentsToImport.push(...droppedFiles);
      this.documentsImporting = false;
    },
    removeFile(file) {
      this.files = this.files.filter(f => {
        return f != file;
      });
    },
    pickDocuments() {
      const input = this.$refs.selectedDocuments;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocuments.click();
    },
    async onDocumentsPicked(e) {
      this.documentsImporting = true;
      const documents = e.target.files;

      if (documents) {
        this.documentsToImport.push(...documents);
        this.documentsImporting = false;
      }
    },

    // NOTE from @jordan: once this is in the React app, it will have its own file and whatnot. This is just an MVP method to get things started.
    async runSchemaValidation(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = function(event) {
          try {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const lowerCased = workbook.SheetNames.map(sn => sn.toLowerCase());

            if (
              !lowerCased.includes('individuals') &&
              !lowerCased.includes('entities')
            ) {
              return resolve({
                isValid: false,
                errors: [
                  'The file must either have an "Individuals" or "Entities" sheet (the spelling must be exact).',
                ],
              });
            }

            workbook.SheetNames.forEach(sheetName => {
              const worksheet = workbook.Sheets[sheetName];
              const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
              });
              const allRowValues = [];

              jsonData.forEach(row => {
                row.forEach(cell => {
                  if (cell) {
                    allRowValues.push(cell);
                  }
                });
              });

              if (
                sheetName === 'Individuals' &&
                !allRowValues.includes('Last Name')
              ) {
                return resolve({
                  isValid: false,
                  errors: ['Sheet "Individuals" is missing "Last Name" column'],
                });
              }

              if (
                sheetName === 'Entities' &&
                !allRowValues.includes('Entity Name')
              ) {
                return resolve({
                  isValid: false,
                  errors: ['Sheet "Entities" is missing "Entity Name" column'],
                });
              }
            });

            resolve({ isValid: true, errors: [] });
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = function(error) {
          reject(error);
        };

        reader.readAsArrayBuffer(file);
      });
    },

    async uploadUserImport() {
      if (this.shouldUseNewSchemaValidations) {
        if (
          this.documentsToImport.length > 1 &&
          this.selectedFileType === 'client_list'
        ) {
          return this.$store.dispatch('application/setErrorNotification', {
            message:
              'Only one file can be uploaded at a time for the Client List file type.',
          });
        }

        if (this.selectedFileType === 'client_list') {
          for (const documentToImport of this.documentsToImport) {
            if (
              !(
                documentToImport.name.toLowerCase().endsWith('xlsx') ||
                documentToImport.name.toLowerCase().endsWith('xls') ||
                documentToImport.name.toLowerCase().endsWith('csv')
              )
            ) {
              return this.$store.dispatch('application/setErrorNotification', {
                message:
                  'The file extension must be .xlsx or .xls for client list import',
              });
            }

            const schemaValidation = await this.runSchemaValidation(
              documentToImport,
            );

            if (!schemaValidation.isValid) {
              return this.$store.dispatch('application/setErrorNotification', {
                message: schemaValidation.errors[0],
              });
            }
          }
        }
      }

      if (
        this.selectedFileType === 'client_list' &&
        this.selectedAssignedFirmUserId === null
      ) {
        return this.$store.dispatch('application/setErrorNotification', {
          message: 'Please select a firm user to whom this should be assigned.',
        });
      }

      if (
        this.selectedFileType === 'client_list' &&
        this.selectedClientListType === null
      ) {
        return this.$store.dispatch('application/setErrorNotification', {
          message: 'Please select a client list type.',
        });
      }

      try {
        let fail = 0;
        this.documentsImporting = true;

        for (const documentToImport of this.documentsToImport) {
          const {
            data: { id, upload_link },
          } = await API.integration.getTemporaryUploadLinkForUserImportDocument();

          await API.integration.uploadToDropbox(upload_link, documentToImport);

          const response = await API.integration.uploadUserImportDocument({
            id,
            documentName: documentToImport.name,
            documentType: this.selectedFileType,
            taxSoftware: this.selectedImportMethod,
            year: this.selectedOrganizerYear,
            comment:
              this.selectedFileType === 'client_list'
                ? this.selectedClientListType
                : this.otherFileName,
            assignedFirmUserId: this.selectedAssignedFirmUserId,
          });

          if (response.data.error) {
            fail += 1;
            console.log(response.data.error);
          }
        }

        if (fail === 0) {
          if (this.selectedFileType === 'client_list') {
            this.$store.dispatch('application/setSuccessNotification', {
              message:
                'Successfully uploaded. Clients will be added within 2 business days, but may take longer depending on the number of clients. We will send you a notification once it is done!',
            });
          } else if (this.selectedFileType === 'zipped_rollover_items') {
            this.$store.dispatch('application/setSuccessNotification', {
              message:
                'Successfully uploaded. Rollover Items will be imported in about 2 business days, but may take longer depending on the number of clients. We will send you a notification once it is done!',
            });
          } else {
            this.$store.dispatch('application/setSuccessNotification', {
              message: 'Successfully uploaded.',
            });
          }
        } else {
          this.$store.dispatch('application/setErrorNotification', {
            message: `There was a problem uploading ${fail} files.`,
          });
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem uploading the file',
        });
      } finally {
        if (this.isSorabanAdmin) {
          this.selectedImportMethod = this.savedTaxOrganizer;
        } else {
          this.selectedImportMethod = null;
        }
        this.selectedOrganizerYear = 2024;
        this.$emit('update:opened', false);
        this.documentsImporting = false;
        this.documentsToImport = [];
        this.otherFileName = '';
        this.selectedFileType = null;
        this.selectedAssignedFirmUserId = null;
      }
    },
  },
  watch: {
    selectedFileType() {
      this.documentsToImport = [];
    },
  },
};
</script>

<style lang="postcss" scoped>
.drag-and-drop {
  height: 140px;
  border-radius: 4px;
  background: #eee;
  border: 1px solid rgba(0, 0, 0, 0.15);

  & >>> i {
    color: rgba(0, 0, 0, 0.3);
  }

  & >>> span {
    color: rgba(0, 0, 0, 0.6);
  }

  & >>> span.clickable {
    cursor: pointer;
  }
}
</style>
